/**
 * The build id of the page that is running. `window.__NEXT_DATA__` is the normal source, but on some devices it
 * has been seen empty after hydration while the `<script id="__NEXT_DATA__">` tag still holds the real value, so
 * fall back to that tag.
 */
export const getClientBuildId = (): string | undefined => {
  const fromWindow = (window as any).__NEXT_DATA__?.buildId;
  if (fromWindow) return fromWindow;
  try {
    return JSON.parse(document.getElementById('__NEXT_DATA__')?.textContent ?? '{}').buildId || undefined;
  } catch {
    return undefined;
  }
};

const fetchServerBuildId = async (): Promise<string | undefined> => {
  try {
    const response = await fetch('/api/build-id/', { cache: 'no-store' });
    if (!response.ok) return undefined;
    return (await response.json()).buildId || undefined;
  } catch {
    return undefined;
  }
};

/**
 * True only on a confirmed mismatch between this page's build and the one the server runs now.
 * Offline, an error or an unknown id is "don't know" (false), so callers never act on a guess.
 */
export const isBuildOutdated = async () => {
  const mine = getClientBuildId();
  const server = mine ? await fetchServerBuildId() : undefined;
  return Boolean(mine && server && mine !== server);
};

/** Confirmed up to date: what warming needs, where "don't know" must also mean "skip". */
export const isBuildCurrent = async () => {
  const mine = getClientBuildId();
  const server = mine ? await fetchServerBuildId() : undefined;
  return Boolean(mine && server && mine === server);
};
